import Input from '@merq/web-ui/dist/components/Input';
import { colors } from '@merq/web-ui/dist/styles/theme';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Grid, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo, useRef } from 'react';
import Select, {
  components,
  ControlProps,
  GroupProps,
  OptionProps,
  SelectInstance,
} from 'react-select';
import makeAnimated from 'react-select/animated';
import useStore from 'store';
import data from '../../common/SSTRIndices.data';
import '../../DecrementForm/widgets/IndexSelector/styles.scss';

interface Option {
  value: string;
  label: string;
  ric?: string;
  ticker?: string;
  company?: string;
  title?: string;
}

interface SelectorProps {
  onChange: any;
  value?: string;
  isDisabled?: boolean;
  color?: 'primary' | 'secondary';
  error?: boolean;
  helperText?: string;
  label?: string;
}

const dataNames = data.map(row => row[0]);

const Selector = React.forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ value, onChange, ...props }: SelectorProps, ref: React.Ref<SelectInstance>) => {
    // TODO: ask if we need a toggle to show/hide development indices
    const indices = useStore(state => state.indices.items);

    const theme = useTheme();
    // Add the custom downloadCSV option if the user is not logged in
    const matches = useRef(new Set<string>());
    // Using fake value bc backend allows mulltiple options to have the same value (multiple indices with the same ID)

    const apiOptions = indices
      .filter(i => isNamespaceAvailable(i.namespace) && !dataNames.includes(i.name))
      .map<Option>(o => ({
        title: o.title,
        label: o.name,
        value: o.name,
      }));

    const nameMap = indices
      .filter(i => isNamespaceAvailable(i.namespace))
      .reduce((acc, curr) => {
        acc[curr.name] = curr.title;
        return acc;
      }, {} as Record<string, string>);

    const dataOptions = data.map<Option>(row => {
      const [indexName, company, ric, ticker] = row;

      return {
        value: indexName,
        label: nameMap[indexName] || indexName,
        ric,
        ticker,
        company,
      };
    });

    const sstrIndices = dataOptions
      .filter(o => o.label && o.value)
      .sort((a, b) => a.label.localeCompare(b.label));

    const otherTRIndices = apiOptions
      .filter(o => o.label && o.value && o.label.includes('_TR_INDEX'))
      .sort((a, b) => a.label.localeCompare(b.label));

    const otherPRIndices = apiOptions
      .filter(o => o.label && o.value && o.label.includes('_PR_INDEX'))
      .sort((a, b) => a.label.localeCompare(b.label));

    const otherNTRIndices = apiOptions
      .filter(o => o.label && o.value && o.label.includes('_NTR_INDEX'))
      .sort((a, b) => a.label.localeCompare(b.label));

    const otherIndices = apiOptions
      .filter(
        o =>
          o.label &&
          o.value &&
          !o.label.includes('_TR_INDEX') &&
          !o.label.includes('_PR_INDEX') &&
          !o.label.includes('_NTR_INDEX') &&
          !FeatureBenchmark.includes(o.value)
      )
      .sort((a, b) => a.label.localeCompare(b.label));

    const featuredBenchmarkIndices = apiOptions
      .filter(o => FeatureBenchmark.includes(o.value))
      .sort((a, b) => a.label.localeCompare(b.label));

    const options = [
      { label: 'Featured Benchmark', options: featuredBenchmarkIndices },
      { label: 'Featured Single Stock Total Return Indices', options: sstrIndices },
      { label: 'Other Total Return Indices', options: otherTRIndices },
      { label: 'Other Price Return Indices', options: otherPRIndices },
      { label: 'Other Net Total Return Indices', options: otherNTRIndices },
      { label: 'Other Indices', options: otherIndices },
    ];

    const resultLimit = 50;
    const handleChange = (changes: unknown) => {
      const values = Array.isArray(changes) ? changes[0] : changes;

      onChange(values.value);
    };
    //eslint-disable-next-line
    const animatedComponents = useMemo(() => makeAnimated(), []);

    const allIndices = [
      ...featuredBenchmarkIndices,
      ...sstrIndices,
      ...otherTRIndices,
      ...otherPRIndices,
      ...otherNTRIndices,
      ...otherIndices,
    ];

    const optionValue = value && allIndices.find(o => o.value === value);

    return options.length ? (
      <Select
        ref={ref}
        isMulti={false}
        value={optionValue ?? ''}
        onChange={handleChange}
        {...props}
        options={options}
        isClearable={false}
        className="decrement-selector"
        classNamePrefix="select"
        data-testid="Identifier"
        inputId="decrement-selector-input"
        closeMenuOnSelect={true}
        backspaceRemovesValue={false}
        placeholder="Type to search"
        filterOption={({ label, data }, query) => {
          const assert =
            label.toUpperCase().indexOf(query.toUpperCase()) >= 0 ||
            ((data as Option)?.value || '').toUpperCase().indexOf(query.toUpperCase()) >= 0 ||
            ((data as Option)?.company || '').toUpperCase().indexOf(query.toUpperCase()) >= 0 ||
            ((data as Option)?.ric || '').toUpperCase().indexOf(query.toUpperCase()) >= 0 ||
            ((data as Option)?.title || '').toUpperCase().indexOf(query.toUpperCase()) >= 0 ||
            ((data as Option)?.ticker || '').toUpperCase().indexOf(query.toUpperCase()) >= 0;

          if (!assert && matches.current.has(label)) {
            matches.current.delete(label);
          }

          if (assert && matches.current.size < resultLimit) {
            matches.current.add(label);
          }

          return matches.current.has(label);
        }}
        onMenuOpen={() => {
          matches.current = new Set<string>();
        }}
        onInputChange={() => {
          matches.current = new Set<string>();
        }}
        components={{
          ...animatedComponents,

          DropdownIndicator: props => (
            <components.DropdownIndicator {...props} data-testid="arrow-down">
              <ArrowDropDownIcon className="icon-add" />
            </components.DropdownIndicator>
          ),
          Option: SelectOption,
          Control,
          Menu,
          Group: ({ label, ...props }: GroupProps) => (
            <components.Group {...props} label="" className="decrement-selector-group">
              {label === 'Featured Single Stock Total Return Indices' ? (
                <div
                  style={{
                    padding: '5px 15px',
                    backgroundColor: theme.palette.background.default,
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    position: 'relative',
                    top: '-15px',
                    marginBottom: '-15px',
                  }}
                >
                  <span
                    style={{
                      textTransform: 'uppercase',
                      opacity: 0.5,
                      fontWeight: 400,
                      letterSpacing: 1,
                      marginBottom: '6px',
                    }}
                  >
                    {label}
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    padding: '5px 15px',
                    backgroundColor: theme.palette.background.default,
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    position: 'relative',
                    top: '-15px',
                    marginBottom: '-15px',
                  }}
                >
                  <span
                    style={{
                      textTransform: 'uppercase',
                      opacity: 0.5,
                      fontWeight: 400,
                      letterSpacing: 1,
                      marginBottom: '6px',
                    }}
                  >
                    {label}
                  </span>
                  <div
                    style={{
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                    }}
                  >
                    <Grid container>
                      <Grid size={5}>Identifier</Grid>
                      <Grid size={7}>Index Name</Grid>
                    </Grid>
                  </div>
                </div>
              )}

              {props.children}
            </components.Group>
          ),
        }}
      />
    ) : (
      <div></div>
    );
  }
);

Selector.displayName = 'IndexSelector';

export default Selector;

const inputComponent = React.forwardRef((props: any, ref) => {
  return <div ref={ref} {...props} />;
});

inputComponent.displayName = 'inputComponent';

const Menu = (props: any) => {
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const backgroundColor = theme.palette.background.paper;

  return (
    <components.Menu {...props}>
      <div
        className="decrement-selector-menu"
        style={{
          borderBottom: `1px solid ${borderColor}`,
          backgroundColor,
        }}
      >
        {props.children}
      </div>
    </components.Menu>
  );
};

const Control = React.forwardRef(
  (props: ControlProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const { children, innerProps, innerRef } = props;
    const value = (props.selectProps.value as Option)?.value;
    const label = (props.selectProps as any).label ?? 'Parent index *';
    const inputLabelProps = value ? { shrink: true } : {};
    const { color, error, helperText } = props.selectProps as any;

    return (
      <Input
        fullWidth
        paper
        color={color}
        data-testid="index-selector"
        variant="outlined"
        label={label}
        value={value}
        name="Identifier"
        onChange={() => {}}
        autoFocus
        error={error}
        helperText={helperText}
        sx={{
          '.decrement-selector-input': {
            display: 'flex',
          },
        }}
        size="normal"
        slotProps={{
          inputLabel: inputLabelProps,
          input: {
            inputComponent,
            classes: {
              input: 'decrement-selector-input ',
            },
            inputProps: {
              ref: innerRef || ref,
              children,
              ...(innerProps as any),
            },
          },
        }}
      />
    );
  }
);

Control.displayName = 'Control';

const SelectOption = React.forwardRef((props: OptionProps, ref) => {
  const data: Option = props.data as any;
  const theme = useTheme();

  const highlightColor = theme.palette.mode === 'dark' ? colors.highlightDark : colors.highlight;

  return (
    <MenuItem
      ref={(ref || props.innerRef) as any}
      selected={props.isFocused}
      component="div"
      className="menu-item"
      {...props.innerProps}
      sx={{
        backgroundColor: theme.palette.background.paper,
        '&: hover': {
          backgroundColor: `${highlightColor} !important`,
        },
        '&.Mui-selected': {
          backgroundColor: `${highlightColor} !important`,
        },
      }}
      data-testid={`index-selector-option-${data.value}`}
    >
      <Grid container sx={{ width: '100%' }}>
        {!data.title ? (
          <Grid overflow="hidden" textOverflow="ellipsis" size={12}>
            {data.label}
          </Grid>
        ) : (
          <>
            <Grid size={5}>{data.label}</Grid>
            <Grid size={7}>{data.title}</Grid>
          </>
        )}
      </Grid>
    </MenuItem>
  );
});

SelectOption.displayName = 'SelectOption';

const availableNamespaces = [
  'default',
  'merqube',
  'merqubetr',
  'fe-onboarding',
  'inqubator',
  'merqubeinqubator',
  'test',
];
const isNamespaceAvailable = (namespace: string) => {
  return availableNamespaces.includes(namespace);
};

const FeatureBenchmark = [
  'MQ5C',
  'MQM',
  'MQMCT',
  'MQ2M',
  'MQ3M',
  'MQBWON',
  'MQBWOL',
  'MQBWOM',
  'MQBWOC',
  'MQBWOS',
  'MQBWOA',
  'MQBER1P',
  'MQBER6P',
  'MQBEZ5P',
  'MQBUK1P',
  'MQBUK3P',
  'MQBDE3P',
  'MQBFR3P',
  'MQBCH2P',
  'MQBE13EP',
  'MQBBX3P',
  'MQBND3P',
  'MQBCA6P',
  'MQBATN',
  'MQBBEN',
  'MQBDEN',
  'MQBHKN',
  'MQBILN',
  'MQBITN',
  'MQBJPN',
  'MQBNLN',
  'MQBNZN',
  'MQBNON',
  'MQBPTN',
  'MQBSGN',
  'MQBESN',
  'MQBSEN',
  'MQBCHN',
];
